import { Identifier } from "react-admin";
import {
  AuditFields,
  ClaimantRef,
  ClientRef,
  CommonFields,
  CommonStatus,
  PartnerRef,
  PolicyRef,
  StoplossClaimRef,
} from "./common";
import { DateYMDString } from "./date";

export const CLAIM_ITEM_TYPES = ["medical", "rx"] as const;
export type ClaimItemType = (typeof CLAIM_ITEM_TYPES)[number];

export const CLAIM_TYPE_CHOICES: { id: ClaimItemType; name: string }[] = [
  { id: "medical", name: "Medical" },
  { id: "rx", name: "Rx" },
];

// Claim item statuses
// TODO: deprecate this -- the individual claim item status is not needed
export enum ClaimItemStatus {
  SUBMITTED = CommonStatus.SUBMITTED,
  PROCESSING = CommonStatus.PROCESSING,
  APPROVED = CommonStatus.APPROVED,
  DENIED = CommonStatus.DENIED,
  PAID = CommonStatus.PAID,
}

export const CLAIM_STATUS_CHOICES: { id: ClaimItemStatus; name: string }[] = [
  { id: ClaimItemStatus.SUBMITTED, name: "Submitted" },
  { id: ClaimItemStatus.PROCESSING, name: "Processing" },
  { id: ClaimItemStatus.APPROVED, name: "Approved" },
  { id: ClaimItemStatus.DENIED, name: "Denied" },
  { id: ClaimItemStatus.PAID, name: "Paid" },
];

/**
 * Base claim item interface - fields that are in both create and read
 */
interface ClaimItemBase extends StoplossClaimRef {
  claim_type: ClaimItemType;
  source_claim_id: string;
  date_of_service_start?: DateYMDString | null;
  date_of_service_end?: DateYMDString | null;
  date_of_service?: DateYMDString | null;
  paid_date?: DateYMDString | null;
  processed_date?: DateYMDString | null;
  adjudication_date?: DateYMDString | null;
  received_date?: DateYMDString | null;
  primary_diagnosis: string;
  procedure_code: string;
  procedure_description: string;
  service_type: string;
  revenue_code: string;
  member_oop: number;
  paid_amount: number;
  billed_amount: number;
  eligible_amount: number;
  deductible_amount: number;
  coinsurance_amount: number;
  copay_amount: number;
  check_number: string;
  provider_type: string;
  provider: string;
  provider_tin: string;
  provider_city: string;
  provider_address: string;
  provider_state: string;
  provider_zip_code: string;
  diagnosis_description: string;
  procedure_modifier: string;
  drug_name: string;
  drug_ndc: string;
  pharmacy_npi: string;
  pharmacy_name: string;
  product_description: string;
  icd_version: string;
  icd: string;
  icd_2: string;
  icd_3: string;
  group_id: string;
  benefit_option: string;
  record_type: string;
  claim_status: ClaimItemStatus;
  is_eligible: boolean;
  /** Eligibility stamp reasons (ADR-081 D5.4); `[]` when eligible. */
  ineligible_reasons?: string[];
}

/**
 * Full claim item record from the API - includes server-managed fields
 */
export interface ClaimItem
  extends ClaimItemBase, CommonFields, AuditFields, ClaimantRef, PolicyRef, ClientRef, PartnerRef {
  source_documents?: Identifier[];
}

/**
 * Payload for creating a new claim item
 * Note: claim_items are created separately after the stoploss claim is created
 */
export interface ClaimItemCreate extends ClaimItemBase, ClaimantRef, ClientRef {}

/**
 * Payload for updating a claim item - all fields optional except id
 */
export interface ClaimItemUpdate extends Partial<ClaimItemBase> {
  id: Identifier;
  // Required fields from ClaimItemBase become optional in updates
  claim_type?: ClaimItemType;
  source_claim_id?: string;
  primary_diagnosis?: string;
  procedure_code?: string;
  claim_status?: ClaimItemStatus;
}
